import { Add, AddCircleOutline, ContentCopy } from '@mui/icons-material';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import React, { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { getActivity } from '../../api/activities';
import { getLockoutTypes, PreciseLockoutTypeCode } from '../../api/documents/lockouts';
import { linkExistingVicToActivity } from '../../api/documents/vics';
import {
    ActivityNimReadDTO,
    DocumentType,
    FeatureFlag,
    ImpactRoleCode,
    ListOfValuesReadDTO,
    LocationDTO,
    NdcReadDTO,
    VicGridReadDTO,
} from '../../api/dto';
import { FieldsAttributesContextProvider } from '../../forms/FieldsAttributesContext';
import { FormGenerator } from '../../forms/FormGenerator';
import { LOCATION_TYPE_ICON } from '../../forms/schemas/location';
import { noteDeCoupureSchema } from '../../forms/schemas/noteDeCoupure';
import { vicSchema } from '../../forms/schemas/vic';
import { useDocumentSaveErrorCallback } from '../../hooks/validation';
import { DimrIcon } from '../../lib/information/dimr';
import { FirePermitIcon } from '../../lib/information/firePermit';
import { Is37Icon } from '../../lib/information/is37';
import { LockoutCryogenicIcon, LockoutElectricalIcon, LockoutIcon } from '../../lib/information/lockout';
import { NdcIcon } from '../../lib/information/noteDeCoupure';
import { EntityData } from '../../lib/information/types/EntityData';
import { EntityType } from '../../lib/information/types/EntityType';
import { VicIcon } from '../../lib/information/vic';
import { translateValue } from '../../lib/language';
import { getRoute, ROUTES } from '../../routes/routes';
import { useApplicationContext } from '../application/ApplicationContextProvider';
import { LinkedDocumentDataAsyncProps, LinkedDocumentsContent } from './LinkedDocumentsContent';

interface Step1Data {
    documentType:
        | DocumentType.LOK
        | DocumentType.FP
        | DocumentType.IS37
        | DocumentType.VIC
        | DocumentType.NDC
        | DocumentType.DIMR;
}

type Step2Data = Step1Data &
    (
        | {
              documentType: DocumentType.LOK;
              lockoutType: ListOfValuesReadDTO<PreciseLockoutTypeCode>;
          }
        | {
              documentType: DocumentType.FP;
              location: LocationDTO | null | undefined;
          }
        | {
              documentType: DocumentType.NDC;
              clone: boolean;
          }
        | {
              documentType: DocumentType.VIC;
              addExisting: boolean;
          }
    );

type Step3Data = Step2Data &
    (
        | {
              documentType: DocumentType.NDC;
              clone: true;
              cloneFrom: NdcReadDTO;
          }
        | {
              documentType: DocumentType.VIC;
              addExisting: true;
              add: VicGridReadDTO;
          }
    );

type WithStep<Step, N extends number> = Step & { step: N };

type StepData = WithStep<{}, 0> | WithStep<Step1Data, 1> | WithStep<Step2Data, 2> | WithStep<Step3Data, 3>;

const initialStepData = { step: 0 } satisfies StepData;

const getRedirectionParameters = (
    data: StepData,
    activityId: number,
    activityLocations: Pick<LocationDTO, 'id'>[]
): Parameters<typeof getRoute>[0] | null => {
    if (data.step === 0) {
        return null;
    }
    const commonQueryParams = { activity: activityId };
    switch (data.documentType) {
        case DocumentType.LOK:
            return data.step === 2
                ? { path: ROUTES.lockout.create, queryParams: { ...commonQueryParams, type: data.lockoutType.code } }
                : null;
        case DocumentType.FP:
            // Automatically select the location if it's the only one or if there is none
            return data.step === 2 || (data.step === 1 && activityLocations.length <= 1)
                ? {
                      path: ROUTES.firePermit.create,
                      queryParams: {
                          ...commonQueryParams,
                          location:
                              (
                                  (data.step === 2 ? data.location : null) ??
                                  (activityLocations.length === 1 ? activityLocations[0] : null)
                              )?.id ?? undefined,
                      },
                  }
                : null;
        case DocumentType.IS37:
            return { path: ROUTES.is37.create, queryParams: commonQueryParams };
        case DocumentType.VIC:
            return data.step === 3
                ? null
                : data.step === 2 && !data.addExisting
                ? { path: ROUTES.vic.create, queryParams: commonQueryParams }
                : null;
        case DocumentType.NDC:
            return (data.step === 2 && !data.clone) || data.step === 3
                ? data.step === 3
                    ? {
                          path: ROUTES.noteDeCoupure.clone,
                          params: { id: data.cloneFrom.id },
                          queryParams: commonQueryParams,
                      }
                    : { path: ROUTES.noteDeCoupure.create, queryParams: commonQueryParams }
                : null;
        case DocumentType.DIMR:
            return { path: ROUTES.dimr.create, queryParams: commonQueryParams };
    }
};

const getRedirection = (...params: Parameters<typeof getRedirectionParameters>): string | null => {
    const parameters = getRedirectionParameters(...params);
    return parameters !== null ? getRoute(parameters) : null;
};

interface StepOption {
    documentType: Step1Data['documentType'];
    nameKey: MessageDescriptor['id'];
    icon: React.ReactNode;
}

const step1Options: StepOption[] = [
    {
        documentType: DocumentType.LOK,
        nameKey: 'lockout.name',
        icon: <LockoutIcon />,
    },
    {
        documentType: DocumentType.FP,
        nameKey: 'firePermit.name',
        icon: <FirePermitIcon />,
    },
    {
        documentType: DocumentType.IS37,
        nameKey: 'is37.name',
        icon: <Is37Icon />,
    },
    {
        documentType: DocumentType.VIC,
        nameKey: 'vic.name',
        icon: <VicIcon />,
    },
    {
        documentType: DocumentType.NDC,
        nameKey: 'ndc.name',
        icon: <NdcIcon />,
    },
    {
        documentType: DocumentType.DIMR,
        nameKey: 'dimr.name',
        icon: <DimrIcon />,
    },
];

interface ActivityAddSafetyDocumentBaseProps {
    activityLocations: LocationDTO[];
}

interface ActivityAddSafetyDocumentDialogContentProps extends ActivityAddSafetyDocumentBaseProps {
    activityId: number;
    onClose: () => void;
    open: boolean;
    refreshData: () => void;
}

const ActivityAddSafetyDocumentDialogContent: React.FC<ActivityAddSafetyDocumentDialogContentProps> = ({
    open,
    onClose,
    activityId,
    activityLocations,
    refreshData,
}) => {
    const [hidden, setHidden] = useState(false);
    const [loading, setLoading] = useState(false);
    const intl = useIntl();
    const form = useForm<StepData>({
        mode: 'onSubmit',
        defaultValues: initialStepData,
    });
    const [step, documentType] = form.watch(['step', 'documentType']);
    const handleFormSaveError = useDocumentSaveErrorCallback(form, DocumentType.ACTIVITY);
    const { currentUser, features } = useApplicationContext();

    const setValues = useCallback(
        (reducer: (oldValues: StepData) => StepData) => {
            const oldValues = form.getValues();
            const newValues = reducer(oldValues);
            form.reset(newValues);
            const redirection = getRedirection(newValues, activityId, activityLocations);
            if (redirection !== null) {
                window.open(redirection, '_blank');
                setHidden(true);
                onClose();
            }
        },
        [form.getValues, form.reset, activityId, onClose]
    );
    const { data: lockoutTypesData, isLoading: isLockoutTypesLoading } = useQuery(
        ['lockoutTypes'],
        () => getLockoutTypes(),
        {
            enabled: open, // Minor optimization
        }
    );

    const { data: activityData } = useQuery(
        ['activity', activityId],
        () => {
            if (activityId === undefined) {
                throw new Error();
            }
            return getActivity({ id: String(activityId) });
        },
        {
            enabled: activityId !== undefined,
        }
    );

    const mutation = useMutation(
        ({ id, vicId }: { id: number; vicId: number }) => linkExistingVicToActivity({ activityId: id, vicId }),
        {
            onMutate: () => {
                setLoading(true);
            },
            onSuccess: () => {
                setLoading(false);
                setHidden(true);
                onClose();
                refreshData();
            },
            onError: (response: AxiosResponse) => {
                handleFormSaveError(response);
                onClose();
            },
        }
    );

    const addExistingVicToActivity = () => {
        const vic = form.getValues('add');
        mutation.mutate({ id: activityId, vicId: vic.id });
    };

    if (hidden) {
        return null;
    }

    return (
        <>
            <DialogTitle>
                <FormattedMessage id="activity.field.activityHazards.addSafetyDocument" />
            </DialogTitle>
            <DialogContent sx={{ pb: 1 }}>
                {loading ? (
                    <LinearProgress />
                ) : (
                    <Stack>
                        <FormProvider {...form}>
                            <FieldsAttributesContextProvider value={undefined} /* Just to avoid scope conflict */>
                                {step === 0 && (
                                    <>
                                        <Typography>
                                            <FormattedMessage id="activity.linkDocument.selectDocument" />
                                        </Typography>
                                        <List>
                                            {step1Options.map(({ documentType: itemDocumentType, nameKey, icon }) => (
                                                <ListItem key={itemDocumentType} disablePadding>
                                                    <ListItemButton
                                                        onClick={() =>
                                                            setValues(() => ({
                                                                step: 1,
                                                                documentType: itemDocumentType,
                                                            }))
                                                        }
                                                    >
                                                        <ListItemIcon>{icon}</ListItemIcon>
                                                        <ListItemText primary={intl.formatMessage({ id: nameKey })} />
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </>
                                )}
                                {step === 1 &&
                                    (documentType === DocumentType.LOK ? (
                                        isLockoutTypesLoading ? (
                                            <LinearProgress />
                                        ) : !lockoutTypesData ? (
                                            <Alert severity="error">
                                                <FormattedMessage id="common.error" />
                                            </Alert>
                                        ) : (
                                            <>
                                                <Typography>
                                                    <FormattedMessage id="activity.linkDocument.selectLockoutType" />
                                                </Typography>
                                                <List>
                                                    {lockoutTypesData.map((lockoutType) => {
                                                        const isElectrical =
                                                            lockoutType.code === PreciseLockoutTypeCode.ELECTRICAL;
                                                        const electricalAllowed =
                                                            !features?.[FeatureFlag.ESP] ||
                                                            (activityData != null &&
                                                                activityData.createdDate != null &&
                                                                new Date(activityData.createdDate) <
                                                                    new Date('2026-08-23')) ||
                                                            !!currentUser?.roleCodes?.includes(
                                                                ImpactRoleCode.IMPACT_C2E
                                                            );
                                                        return (
                                                            <ListItem key={lockoutType.code} disablePadding>
                                                                <Tooltip
                                                                    title={
                                                                        isElectrical && !electricalAllowed
                                                                            ? intl.formatMessage({
                                                                                  id: 'activity.linkDocument.lockoutElectrical.disabled',
                                                                              })
                                                                            : ''
                                                                    }
                                                                >
                                                                    <span>
                                                                        <ListItemButton
                                                                            disabled={
                                                                                isElectrical && !electricalAllowed
                                                                            }
                                                                            onClick={() =>
                                                                                setValues(() => ({
                                                                                    step: 2,
                                                                                    documentType: DocumentType.LOK,
                                                                                    lockoutType,
                                                                                }))
                                                                            }
                                                                        >
                                                                            <ListItemIcon>
                                                                                {
                                                                                    {
                                                                                        [PreciseLockoutTypeCode.ELECTRICAL]:
                                                                                            <LockoutElectricalIcon />,
                                                                                        [PreciseLockoutTypeCode.CRYO]: (
                                                                                            <LockoutCryogenicIcon />
                                                                                        ),
                                                                                    }[lockoutType.code]
                                                                                }
                                                                            </ListItemIcon>
                                                                            <ListItemText
                                                                                primary={
                                                                                    translateValue(
                                                                                        lockoutType,
                                                                                        intl.locale
                                                                                    ).label
                                                                                }
                                                                            />
                                                                        </ListItemButton>
                                                                    </span>
                                                                </Tooltip>
                                                            </ListItem>
                                                        );
                                                    })}
                                                </List>
                                            </>
                                        )
                                    ) : documentType === DocumentType.FP ? (
                                        <>
                                            <Typography>
                                                <FormattedMessage id="activity.linkDocument.selectLocationFirePermit" />
                                            </Typography>
                                            <List>
                                                {activityLocations.map((activityLocation, i) => (
                                                    <ListItem key={`${activityLocation.id}-${i}`} disablePadding>
                                                        <ListItemButton
                                                            onClick={() =>
                                                                setValues(() => ({
                                                                    step: 2,
                                                                    documentType: DocumentType.FP,
                                                                    location: activityLocation,
                                                                }))
                                                            }
                                                        >
                                                            <ListItemIcon>
                                                                {LOCATION_TYPE_ICON[activityLocation.locationType]}
                                                            </ListItemIcon>
                                                            <ListItemText primary={activityLocation.impactName} />
                                                        </ListItemButton>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </>
                                    ) : documentType === DocumentType.NDC ? (
                                        <Stack direction="column" spacing={2}>
                                            <Typography>
                                                <FormattedMessage id="activity.linkDocument.selectClone" />
                                            </Typography>
                                            <List>
                                                {[false, true].map((clone) => (
                                                    <ListItem key={`${clone}`} disablePadding>
                                                        <ListItemButton
                                                            onClick={() =>
                                                                setValues(() => ({
                                                                    step: 2,
                                                                    documentType: DocumentType.NDC,
                                                                    clone,
                                                                }))
                                                            }
                                                        >
                                                            <ListItemIcon>
                                                                {clone ? <ContentCopy /> : <Add />}
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={intl.formatMessage({
                                                                    id: clone
                                                                        ? 'activity.linkDocument.selectCloneAnswer.yes'
                                                                        : 'activity.linkDocument.selectCloneAnswer.no',
                                                                })}
                                                            />
                                                        </ListItemButton>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Stack>
                                    ) : documentType === DocumentType.VIC ? (
                                        <Stack direction="column" spacing={2}>
                                            <Typography>
                                                <FormattedMessage id="activity.linkDocument.selectExisting" />
                                            </Typography>
                                            <List>
                                                {[false, true].map((addExisting) => (
                                                    <ListItem key={`${addExisting}`} disablePadding>
                                                        <ListItemButton
                                                            onClick={() =>
                                                                setValues(() => ({
                                                                    step: 2,
                                                                    documentType: DocumentType.VIC,
                                                                    addExisting,
                                                                }))
                                                            }
                                                        >
                                                            <ListItemIcon>
                                                                {addExisting ? <ContentCopy /> : <Add />}
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={intl.formatMessage({
                                                                    id: addExisting
                                                                        ? 'activity.linkDocument.selectExistingAnswer.yes'
                                                                        : 'activity.linkDocument.selectExistingAnswer.no',
                                                                })}
                                                            />
                                                        </ListItemButton>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Stack>
                                    ) : null)}
                                {step === 2 &&
                                    (documentType === DocumentType.NDC ? (
                                        <Stack direction="column" spacing={2}>
                                            <Typography>
                                                <FormattedMessage id="activity.linkDocument.selectCloneDocument" />
                                            </Typography>
                                            <FormGenerator
                                                rootElement={noteDeCoupureSchema(
                                                    {
                                                        path: 'cloneFrom',
                                                        label: intl.formatMessage({
                                                            id: 'ndc.name',
                                                        }),
                                                    },
                                                    intl
                                                )}
                                                form={form}
                                            />
                                        </Stack>
                                    ) : documentType === DocumentType.VIC ? (
                                        <Stack direction="column" spacing={2}>
                                            <Typography>
                                                <FormattedMessage id="activity.linkDocument.selectExistingDocument" />
                                            </Typography>
                                            <FormGenerator
                                                rootElement={vicSchema(
                                                    {
                                                        path: 'add',
                                                        label: intl.formatMessage({
                                                            id: 'vic.name',
                                                        }),
                                                    },
                                                    intl
                                                )}
                                                form={form}
                                            />
                                        </Stack>
                                    ) : null)}
                            </FieldsAttributesContextProvider>
                        </FormProvider>
                    </Stack>
                )}
            </DialogContent>
            {!loading && (
                <DialogActions>
                    <Button onClick={onClose} sx={{ color: grey[600] }}>
                        <FormattedMessage id="common.cancel" />
                    </Button>
                    {step === 2 && documentType === DocumentType.NDC ? (
                        <Button onClick={() => setValues((state) => ({ ...(state as Step3Data), step: 3 }))}>
                            <FormattedMessage id="common.confirm" />
                        </Button>
                    ) : documentType === DocumentType.VIC ? (
                        <Button onClick={addExistingVicToActivity}>
                            <FormattedMessage id="common.confirm" />
                        </Button>
                    ) : null}
                </DialogActions>
            )}
        </>
    );
};

interface ActivityAddSafetyDocumentDialogProps extends ActivityAddSafetyDocumentDialogContentProps {}

const ActivityAddSafetyDocumentDialog: React.FC<ActivityAddSafetyDocumentDialogProps> = (props) => {
    const { open, onClose } = props;
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" keepMounted={false}>
            <ActivityAddSafetyDocumentDialogContent {...props} />
        </Dialog>
    );
};

interface ActivityAddSafetyDocumentButtonProps extends ActivityAddSafetyDocumentBaseProps {
    linkedSafetyDocuments: Pick<
        ActivityNimReadDTO,
        'notesDeCoupure' | 'firePermits' | 'is37s' | 'vics' | 'lockouts' | 'rpAssessment'
    > | null;
    activityId: number | null;
    readOnly?: boolean;
    disabled?: boolean;
    title?: string;
    refreshData: () => void;
}

export const ActivityAddSafetyDocumentButton: React.FC<ActivityAddSafetyDocumentButtonProps> = ({
    activityId,
    linkedSafetyDocuments,
    readOnly,
    disabled,
    title,
    refreshData,
    ...rest
}) => {
    const [open, setOpen] = useState(false);
    type DatedEntityData = EntityData & { document: { createdDate: string } };
    const linkedDocumentsContent: LinkedDocumentDataAsyncProps[] = useMemo(() => {
        return [
            ...(linkedSafetyDocuments?.lockouts?.map(
                (lockout) =>
                    ({
                        type: EntityType.Lockout,
                        lockout,
                        document: lockout,
                    } satisfies DatedEntityData)
            ) ?? []),
            ...(linkedSafetyDocuments?.firePermits?.map(
                (firePermit) =>
                    ({
                        type: EntityType.FirePermit,
                        firePermit,
                        document: firePermit,
                    } satisfies DatedEntityData)
            ) ?? []),
            ...(linkedSafetyDocuments?.is37s?.map(
                (is37) =>
                    ({
                        type: EntityType.Is37,
                        is37,
                        document: is37,
                    } satisfies DatedEntityData)
            ) ?? []),
            ...(linkedSafetyDocuments?.vics?.map(
                (vic) =>
                    ({
                        type: EntityType.Vic,
                        vic,
                        document: vic,
                    } satisfies DatedEntityData)
            ) ?? []),
            ...(linkedSafetyDocuments?.notesDeCoupure?.map(
                (noteDeCoupure) =>
                    ({
                        type: EntityType.NoteDeCoupure,
                        noteDeCoupure,
                        document: noteDeCoupure,
                    } satisfies DatedEntityData)
            ) ?? []),
            ...(linkedSafetyDocuments?.rpAssessment?.dimrVersion
                ? [linkedSafetyDocuments?.rpAssessment.dimrVersion].map(
                      (dimrVersion) =>
                          ({
                              type: EntityType.Dimr,
                              dimr: dimrVersion,
                              document: dimrVersion,
                          } satisfies DatedEntityData)
                  )
                : []),
        ].sort(
            ({ document: { createdDate: d1 } }, { document: { createdDate: d2 } }) =>
                new Date(d1).getTime() - new Date(d2).getTime() // Ascending (oldest first)
        );
    }, [linkedSafetyDocuments]);
    return (
        <>
            {activityId !== null && (
                <>
                    <ActivityAddSafetyDocumentDialog
                        {...rest}
                        activityId={activityId}
                        open={open}
                        onClose={() => setOpen(false)}
                        refreshData={refreshData}
                    />
                    <LinkedDocumentsContent documents={linkedDocumentsContent} />
                </>
            )}
            {!readOnly && (
                <Tooltip title={title}>
                    <span>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => setOpen(true)}
                            disabled={disabled}
                            startIcon={<AddCircleOutline />}
                        >
                            <FormattedMessage id="activity.field.activityHazards.addSafetyDocument" />
                        </Button>
                    </span>
                </Tooltip>
            )}
        </>
    );
};
